import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SocialIcons from "./SocialIcons";
import { Phone, ChevronDown, Menu, X } from "lucide-react";
import { SITE } from "../../lib/siteData";

const NAV = [
  { label: "Home", href: "/#top" },
  {
    label: "Services",
    href: "/#services",
    dropdown: SITE.services.map((s) => ({
      label: s.title,
      href: s.href,
    })),
  },
  { label: "Why Us", href: "/#why" },
  { label: "Team", href: "/#team" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "Contact", href: "/#quote" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
    // setScrolled is stable; handler is defined inline — mount-only effect.
  }, []);

  useEffect(() => {
    setOpen(false);
    // setOpen is stable per React's useState contract.
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-[#011233] text-white" data-testid="top-strip">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-2.5 flex items-center justify-between gap-4 text-[14px] tracking-[0.12em] uppercase text-white">
          <div className="flex items-center gap-5 flex-wrap text-white">
            <a
              href={`tel:${SITE.toll_free_tel}`}
              className="flex items-center gap-2 text-white font-bold hover:text-[#B5BAC2] transition-colors"
              data-testid="top-strip-tollfree"
            >
              <Phone size={14} />
              <span>{SITE.toll_free}</span>
            </a>
            <a
              href={`tel:${SITE.phone.replace(/[^\d]/g, "")}`}
              className="flex items-center gap-2 text-white font-bold hover:text-[#B5BAC2] transition-colors"
              data-testid="top-strip-phone"
            >
              <Phone size={14} />
              <span>1 {SITE.phone}</span>
            </a>
          </div>
          <SocialIcons
            size={16}
            className="ml-auto"
            linkClassName="text-white hover:text-[#B5BAC2] transition-colors p-1"
          />
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-300 bg-white ${
          scrolled ? "shadow-sm border-b border-black/5" : "border-b border-transparent"
        }`}
      >
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between gap-4 h-[125px]">
          <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
            <img
              src="/knight-logo-hero.png"
              alt="Knight Insurance Solutions"
              className="h-[124px] w-auto"
              style={{ objectFit: "contain" }}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
            {NAV.map((item) =>
              item.dropdown ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#011233] transition-colors"
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                    <ChevronDown size={14} />
                  </button>
                  {servicesOpen && (
                    <div className="absolute left-0 top-full pt-2 w-64">
                      <div className="rounded-2xl border border-black/5 bg-white/95 backdrop-blur-xl shadow-xl shadow-slate-900/5 p-2">
                        {item.dropdown.map((d) => {
                          const isInternal = d.href && d.href.startsWith("/") && !d.href.startsWith("/#");
                          const props = {
                            className:
                              "block px-4 py-2.5 text-sm rounded-xl text-slate-700 hover:bg-slate-50 hover:text-[#011233] transition-colors",
                            "data-testid": `nav-dropdown-${d.label.toLowerCase().replace(/\s/g, "-")}`,
                          };
                          return isInternal ? (
                            <Link key={d.label} to={d.href} {...props}>
                              {d.label}
                            </Link>
                          ) : (
                            <a key={d.label} href={d.href} {...props}>
                              {d.label}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#011233] transition-colors whitespace-nowrap"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/agent/login"
              className="hidden sm:inline-flex items-center rounded-full border border-[#011233] text-[#011233] hover:bg-[#011233] hover:text-white transition-colors px-5 py-2.5 text-sm font-medium whitespace-nowrap"
              data-testid="nav-client-portal"
            >
              Client Portal
            </Link>
            <a
              href="/#quote"
              className="kn-btn-primary inline-flex items-center rounded-full px-5 py-2.5 text-sm font-medium whitespace-nowrap"
              data-testid="nav-cta-quote"
            >
              Get A Quote
            </a>
            <button
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            className="lg:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="py-2.5 text-base font-medium text-slate-800"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <Link
                to="/agent/login"
                className="py-2.5 text-base font-medium text-slate-800"
                data-testid="mobile-nav-client-portal"
              >
                Client Portal
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
